import React from "react";

/**
 * SuiteButton — bottone unificato della Gruppo Impresa Suite.
 * Sostituisce l'uso diretto di <Button> con stili hardcoded (bg-emerald-*, ecc.).
 *
 * Gerarchia stretta:
 *  - primary   : verde emerald (CTA principali: Salva, Conferma, Applica)
 *  - secondary : bianco con bordo (azioni secondarie)
 *  - danger    : rosso (azioni distruttive)
 *  - ghost     : trasparente (link-like)
 *  - suite     : verde petrolio (identità Suite, uso raro — brand button)
 *
 * Size:
 *  - sm  : 28px altezza (h-7)
 *  - md  : 36px altezza (h-9) — default
 *  - lg  : 44px altezza (h-11)
 *
 * Props:
 *  - variant, size, icon, iconRight, loading, disabled, type, className, children
 *  - as: se serve renderizzare <a> invece di <button>, passare "a"
 */
export const SuiteButton = React.forwardRef(function SuiteButton(
  {
    children,
    variant = "primary",
    size = "md",
    icon: Icon,
    iconRight: IconRight,
    loading = false,
    disabled = false,
    type = "button",
    className = "",
    as = "button",
    ...rest
  },
  ref
) {
  const variantMap = {
    primary:
      "bg-suite-accent hover:bg-suite-accent-hover text-white border border-transparent shadow-sm",
    secondary:
      "bg-white hover:bg-suite-neutral-50 text-suite-neutral-700 border border-suite-neutral-200 hover:border-suite-neutral-300",
    danger:
      "bg-suite-error hover:bg-suite-error-hover text-white border border-transparent shadow-sm",
    ghost:
      "bg-transparent hover:bg-suite-neutral-100 text-suite-neutral-700 border border-transparent",
    suite:
      "bg-suite-primary hover:bg-suite-primary-hover text-white border border-transparent shadow-sm",
    outline:
      "bg-transparent hover:bg-suite-primary-light text-suite-primary border border-suite-primary-border",
  };
  const sizeMap = {
    sm: "h-7 px-2.5 text-xs gap-1.5",
    md: "h-9 px-3.5 text-sm gap-2",
    lg: "h-11 px-5 text-sm gap-2",
  };
  const iconSize = size === "lg" ? 16 : size === "sm" ? 13 : 14;
  const isDisabled = disabled || loading;

  const cls = [
    "inline-flex items-center justify-center rounded-md font-semibold whitespace-nowrap",
    "transition-all duration-150 ease-out",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-suite-primary/30 focus-visible:ring-offset-1",
    "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
    "active:scale-[0.98]",
    variantMap[variant] || variantMap.primary,
    sizeMap[size] || sizeMap.md,
    className,
  ].join(" ");

  const Tag = as;
  return (
    <Tag ref={ref} type={as === "button" ? type : undefined} className={cls} disabled={isDisabled} {...rest}>
      {loading ? (
        <span
          className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"
          aria-hidden="true"
        />
      ) : Icon ? (
        <Icon size={iconSize} strokeWidth={2} />
      ) : null}
      {children}
      {IconRight ? <IconRight size={iconSize} strokeWidth={2} /> : null}
    </Tag>
  );
});

export default SuiteButton;
